import "./index.css";

import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { useQuoteBoardReturn } from "~/features/quote-board/hooks/use-quote-board-return.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import {
	useAutoSpinner,
	useGlobalSpinner,
} from "~/shared/hooks/use-global-spinner.ts";

import { useQuoteAddressContext } from "../../context/quote-address-context.tsx";
import { QuoteDetailsContext } from "../../context/quote-details-context.tsx";
import {
	useQuoteDetailsPageLoadQueries,
	useShipmentDetailsHandlers,
	useShipmentDetailsLoader,
} from "../../hooks/use-shipment-details-hooks.ts";
import { useShipmentValidation } from "../../hooks/use-shipment-validation.ts";
import { hasAddressFieldOverLimit } from "../../utils/has-address-field-over-limit.ts";
import { mapItemToHandlingUnit } from "../../utils/map-item-to-handling-unit.ts";
import { AdditionalDetailsSection } from "./components/additional-details-section/additional-details-section.tsx";
import { BillingCustomerDropdown } from "./components/billing-customer-dropdown/billing-customer-dropdown.tsx";
import { ConfirmationModal } from "./components/confirmation-modal/confirmation-modal.tsx";
import { DetailsNeededBanner } from "./components/details-needed-banner/details-needed-banner.tsx";
import { ItemDetails } from "./components/shipment-item-details/shipment-item-details.tsx";
import { ShipmentPickupDetails } from "./components/shipment-pickup-details/shipment-pickup-details.tsx";
import { TenderModal } from "./components/tender-modal/tender-modal.tsx";
import { TermsOfService } from "./components/terms-of-service/terms-of-service.tsx";
import { getMissingDetailLabels } from "./utils/missing-detail-labels.ts";

export const ShipmentDetailsForm = () => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { isInternal } = useDeploymentType();
	const {
		validationErrors,
		validate,
		clearFieldError,
		clearItemFieldError,
		hasFieldError,
	} = useShipmentValidation();

	const handleBeforeSubmit = useCallback(() => {
		return validate(quoteState, selectedAddressBookItems, isInternal);
	}, [validate, quoteState, selectedAddressBookItems, isInternal]);

	const isAnyFieldOverLimit = hasAddressFieldOverLimit(
		selectedAddressBookItems,
	);

	// Clear any hanging manual spinners from previous page (e.g., carrier booking)
	const { hideSpinner } = useGlobalSpinner();
	useEffect(() => {
		// Clear any potential hanging manual spinner from carrier booking
		hideSpinner();
	}, [hideSpinner]);

	// Use consolidated quote loader for DRY compliance
	const quoteLoader = useShipmentDetailsLoader();

	// Use extracted handlers for modals and loading states
	const {
		showModal,
		isSaving,
		handleShowConfirmationModal,
		handleShowTenderModal,
		handleShowErrorModal,
		handleIsSaving,
		errorMessage,
	} = useShipmentDetailsHandlers();

	// Track initial loading phase - start as false if quote is already loaded
	const [isInitialLoad, setIsInitialLoad] = useState(() => {
		// If there's no need to fetch a quote (already loaded), start with false
		return quoteLoader.shouldFetchQuote;
	});

	const isInitialQuoteLoading =
		quoteLoader.shouldFetchQuote &&
		quoteLoader.isLoadingQuote &&
		!quoteLoader.quoteIsFetched;

	const { data: pageData, isFetching: pageDataIsFetching } =
		useQuoteDetailsPageLoadQueries();

	const isLoading =
		isSaving || isInitialQuoteLoading || isInitialLoad || pageDataIsFetching;

	// Check if all other loading conditions are met (excluding isInitialLoad to avoid circular dependency)
	const isAllDataReady =
		!isSaving &&
		(!quoteLoader.shouldFetchQuote ||
			!quoteLoader.isLoadingQuote ||
			quoteLoader.quoteIsFetched) &&
		!pageDataIsFetching;

	// Mark initial load as complete when all data is ready
	useEffect(() => {
		if (isAllDataReady && isInitialLoad) {
			setIsInitialLoad(false);
		}
	}, [isAllDataReady, isInitialLoad]);

	const mappedItems = useMemo(() => {
		return quoteState.items.map((item) => mapItemToHandlingUnit(item));
	}, [quoteState.items]);

	// Automatically manage global spinner for page data loading
	useAutoSpinner(isLoading);

	// If the quote has no selected carrier, send the user back to carrier
	// selection (e.g. after a margin change cleared the prior selection).
	const navigate = useNavigate();
	const location = useLocation();
	const { returnTo, returnToQuoteBoard } = useQuoteBoardReturn();
	const isShipmentDetailsPage = location.pathname.startsWith(
		routes.shipmentDetails.replace("/:quoteId", ""),
	);

	// A `returnTo` back to the Quote Board means the user arrived from a bulk
	// tender "Details Needed" row, so surface the missing fields up front
	// instead of waiting for them to press Tender.
	const isDetailsNeededRepair = returnTo != null && isShipmentDetailsPage;
	const hasPromptedForMissingDetails = useRef(false);
	useEffect(() => {
		if (isLoading || !isDetailsNeededRepair || quoteState.id <= 0) return;
		if (hasPromptedForMissingDetails.current) return;
		hasPromptedForMissingDetails.current = true;
		validate(quoteState, selectedAddressBookItems);
	}, [
		isLoading,
		isDetailsNeededRepair,
		validate,
		quoteState,
		selectedAddressBookItems,
	]);

	const missingDetails = useMemo(
		() =>
			isDetailsNeededRepair
				? getMissingDetailLabels(
						validationErrors,
						quoteState.items.map((item) => item.id),
					)
				: [],
		[isDetailsNeededRepair, validationErrors, quoteState.items],
	);
	useEffect(() => {
		if (isLoading) {
			return;
		}
		if (quoteState.id > 0 && !quoteState.selectedCarrierPriceId) {
			const carrierSelectionRoute = location.pathname.startsWith(
				routes.ltlShipment,
			)
				? routes.ltlShipmentCarrierSelection
				: routes.carrierSelection;
			navigate(
				carrierSelectionRoute.replace(":quoteId", String(quoteState.id)),
				{ replace: true },
			);
		}
	}, [
		isLoading,
		quoteState.id,
		quoteState.selectedCarrierPriceId,
		navigate,
		location.pathname,
	]);

	if (isLoading) {
		return null;
	}
	return (
		<div className={"shipment-details-wrapper"}>
			<ConfirmationModal
				onToggleModal={handleShowConfirmationModal}
				showModal={showModal.confirmation}
			/>
			<TenderModal
				onToggleModal={handleShowTenderModal}
				showModal={showModal.tender}
			/>
			{showModal.error ? (
				<TraxModal
					onClose={() => handleShowErrorModal(false)}
					title={"Tender Failed"}
					type={"error"}
					width={500}
				>
					<p>{errorMessage}</p>
				</TraxModal>
			) : null}
			<BillingCustomerDropdown
				className={
					isShipmentDetailsPage ? "billing-customer-wrapper" : undefined
				}
			/>
			<DetailsNeededBanner missingDetails={missingDetails} />
			<div id={"pickup-delivery-wrapper"}>
				<div id={"pickup-wrapper"}>
					<h2>Pickup</h2>
					<ShipmentPickupDetails
						hasFieldError={hasFieldError}
						holidays={pageData?.holidays}
						isSaving={isSaving}
						onClearFieldError={clearFieldError}
						onSavingStateChange={handleIsSaving}
						type={"pickup"}
					/>
				</div>

				<div id={"delivery-wrapper"}>
					<h2>Delivery</h2>
					<ShipmentPickupDetails
						hasFieldError={hasFieldError}
						holidays={pageData?.holidays}
						isSaving={isSaving}
						onClearFieldError={clearFieldError}
						onSavingStateChange={handleIsSaving}
						type={"delivery"}
					/>
				</div>
			</div>
			<hr className={"tql-divider "} />
			<div id={"commodity-wrapper"}>
				<h2>Handling Units</h2>
				<ItemDetails
					itemValidationErrors={validationErrors.items}
					items={mappedItems}
					onClearItemFieldError={clearItemFieldError}
				/>
			</div>
			<hr className={"tql-divider "} />
			<div id={"additional-details-wrapper"}>
				<AdditionalDetailsSection
					hasFieldError={hasFieldError}
					onClearFieldError={clearFieldError}
				/>
			</div>
			<hr className={"tql-divider "} />
			<div id={"terms-of-service-wrapper"}>
				<TermsOfService
					handleShowConfirmationModal={handleShowConfirmationModal}
					handleShowErrorModal={handleShowErrorModal}
					handleShowTenderModal={handleShowTenderModal}
					isTenderDisabled={isAnyFieldOverLimit}
					onBeforeSubmit={handleBeforeSubmit}
					onSaveSuccess={returnTo ? returnToQuoteBoard : undefined}
				/>
			</div>
		</div>
	);
};
